import PiInformationCircleSolid from "@markeroo/icons/src/solid/alerts/PiInformationCircleSolid";

import { useGCStatus } from "@/api/hooks";
import { useFeatureFlag } from "@/providers/FeatureFlagProvider";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";
import { API_ORIGIN } from "@/api/config";
import { ui } from "@/strings";

/**
 * Banner that appears when the gc_student_writing feature flag is enabled
 * but the user hasn't granted the required write scopes.
 *
 * This shows when:
 * - gc_student_writing feature flag is enabled
 * - User has gc_mode enabled (they connected to Google Classroom)
 * - User has a valid refresh token (connection is healthy)
 * - But the user hasn't granted write scopes yet
 *
 * The banner prompts the user to reconnect to grant additional permissions
 * needed for student writing functionality.
 */
export function GCWriteScopesBanner() {
  const gcIntegrationEnabled = useFeatureFlag("gc_integration");
  const gcStudentWritingEnabled = useFeatureFlag("gc_student_writing");
  const { user } = useAuthStore(useShallow((state) => ({ user: state.user })));
  const { data: gcStatus, isLoading } = useGCStatus({
    enabled: gcIntegrationEnabled && !!user,
  });

  // Don't show if:
  // - GC integration flag is off
  // - gc_student_writing flag is off (no need for write scopes)
  // - User is not logged in
  // - Still loading GC status
  // - GC mode is not enabled (user never connected)
  // - Refresh token is missing (GCReconnectBanner handles this case)
  // - User already has write scopes
  if (
    !gcIntegrationEnabled ||
    !gcStudentWritingEnabled ||
    !user ||
    isLoading ||
    !gcStatus?.gc_mode ||
    !gcStatus?.has_refresh_token ||
    gcStatus?.has_write_scopes
  ) {
    return null;
  }

  const handleGrantPermissions = () => {
    // Redirect to Google Classroom OAuth flow, returning to current page
    const nextPath = window.location.pathname;
    window.location.href = `${API_ORIGIN}/api/google-classroom/connect?next=${encodeURIComponent(nextPath)}`;
  };

  return (
    <div className="bg-semantic-warning text-white px-4 py-4">
      <div className="mx-auto max-w-7xl flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <PiInformationCircleSolid size={24} className="flex-shrink-0" />
          <div>
            <p className="font-semibold">
              {ui.googleClassroom.writeScopesRequiredTitle}
            </p>
            <p className="text-sm text-semantic-warning-light">
              {ui.googleClassroom.writeScopesRequiredDescription}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleGrantPermissions}
          className="rounded-md bg-white px-4 py-2 text-sm font-medium text-semantic-warning transition hover:bg-semantic-warning-light flex-shrink-0"
        >
          {ui.googleClassroom.grantPermissions}
        </button>
      </div>
    </div>
  );
}
